import React, { useState } from "react";
import ReactCountryFlag from "react-country-flag";
import {
  usePlayers,
  useDeletePlayer,
  useUploadPlayerPhoto,
} from "../apiCalls/playerApiCalls";
import {
  Badge,
  Button,
  Card,
  Cell,
  EmptyState,
  ErrorState,
  Input,
  Loader,
  PageHeader,
  Row,
  Select,
  Table,
} from "../components/ui";
import ConfirmDelete from "../components/ConfirmDelete";
import PlayerForm from "./PlayerForm";

const HEAD = [
  { label: "Player" },
  { label: "Club" },
  { label: "Status" },
  { label: "Account" },
  { label: "Points", align: "right" },
  { label: "", key: "actions", align: "right" },
];

const PAGE_SIZE = 25;

export default function Players() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [claimed, setClaimed] = useState("");
  const [page, setPage] = useState(1);

  const [editing, setEditing] = useState(null); // player | "new" | null
  const [deleting, setDeleting] = useState(null);

  const query = usePlayers({
    search,
    status,
    claimed,
    page,
    limit: PAGE_SIZE,
  });
  const { mutate: remove, isLoading: isDeleting } = useDeletePlayer();
  const { mutate: uploadPhoto } = useUploadPlayerPhoto();

  // Any filter change invalidates the current page number.
  const withReset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const players = query.data?.data ?? [];
  const totalResults = query.data?.totalResults ?? 0;
  const totalPages = query.data?.totalPages ?? 1;

  return (
    <>
      <PageHeader
        title="Players"
        subtitle="Everyone in the register. Most were entered from an entry sheet and have no account — they rank all the same."
        actions={
          <Button variant="primary" onClick={() => setEditing("new")}>
            Add player
          </Button>
        }
      />

      <Card className="mb-6 flex flex-wrap gap-3 p-4">
        <Input
          className="w-full sm:w-64"
          placeholder="Search by name"
          value={search}
          onChange={(event) => withReset(setSearch)(event.target.value)}
        />
        <Select
          className="w-full sm:w-40"
          value={status}
          onChange={(event) => withReset(setStatus)(event.target.value)}
        >
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Select>
        <Select
          className="w-full sm:w-48"
          value={claimed}
          onChange={(event) => withReset(setClaimed)(event.target.value)}
        >
          <option value="">Claimed or not</option>
          <option value="false">No account</option>
          <option value="true">Has an account</option>
        </Select>
        <div className="ml-auto self-center font-mono text-xs text-ink-soft tabular">
          {totalResults} player{totalResults === 1 ? "" : "s"}
        </div>
      </Card>

      <Card>
        {query.isLoading ? (
          <Loader label="Loading the register" />
        ) : query.isError ? (
          <ErrorState
            message="Couldn't load the register."
            onRetry={query.refetch}
          />
        ) : players.length === 0 ? (
          <EmptyState
            message={
              search || status || claimed
                ? "No players match these filters."
                : "The register is empty."
            }
            action={
              <Button variant="primary" onClick={() => setEditing("new")}>
                Add the first player
              </Button>
            }
          />
        ) : (
          <Table head={HEAD}>
            {players.map((player) => (
              <Row key={player._id}>
                <Cell>
                  <div className="flex items-center gap-3">
                    <img
                      src={
                        player.imgUrl ||
                        `${process.env.PUBLIC_URL}/default-user.jpg`
                      }
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-full object-cover"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 font-medium">
                        <span className="truncate">{player.name}</span>
                        {player.nationality && (
                          <ReactCountryFlag
                            countryCode={player.nationality}
                            svg
                            style={{ width: "1.1em", height: "1.1em" }}
                            aria-label={player.nationality}
                          />
                        )}
                      </div>
                      <div className="font-mono text-xs text-ink-soft">
                        /{player.slug}
                      </div>
                    </div>
                  </div>
                </Cell>
                <Cell>
                  {player.club?.name || (
                    <span className="text-ink-soft">Independent</span>
                  )}
                </Cell>
                <Cell>
                  <Badge tone={player.status === "active" ? "active" : "pending"}>
                    {player.status === "active" ? "Active" : "Inactive"}
                  </Badge>
                </Cell>
                <Cell>
                  {player.isClaimed ? (
                    <div className="min-w-0">
                      <Badge tone="active">Claimed</Badge>
                      {/* Which account owns it, not just that one does. */}
                      {player.accountEmail && (
                        <div
                          className="mt-1 truncate font-mono text-xs text-ink-soft"
                          title={player.accountEmail}
                        >
                          {player.accountEmail}
                        </div>
                      )}
                      {player.accountStatus === "suspended" && (
                        <div className="mt-1">
                          <Badge tone="danger">Suspended</Badge>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Badge>No account</Badge>
                  )}
                </Cell>
                <Cell align="right" className="font-mono tabular">
                  {player.rankingPoints}
                </Cell>
                <Cell align="right">
                  <div className="flex justify-end gap-2">
                    <label className="cursor-pointer border border-sand px-3 py-1.5 text-xs hover:bg-teal-wash">
                      Photo
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) uploadPhoto({ id: player._id, file });
                          event.target.value = "";
                        }}
                      />
                    </label>
                    <Button
                      className="px-3 py-1.5 text-xs"
                      onClick={() => setEditing(player)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      className="px-3 py-1.5 text-xs"
                      onClick={() => setDeleting(player)}
                    >
                      Delete
                    </Button>
                  </div>
                </Cell>
              </Row>
            ))}
          </Table>
        )}
      </Card>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <span className="font-mono text-xs text-ink-soft tabular">
            Page {page} of {totalPages}
          </span>
          <Button
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {editing && (
        <PlayerForm
          player={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {deleting && (
        <ConfirmDelete
          title={`Delete ${deleting.name}?`}
          body={
            deleting.isClaimed
              ? "This removes the player from the register and from the rankings. Their account stays, but will no longer be linked to a profile."
              : "This removes the player from the register and from the rankings."
          }
          isDeleting={isDeleting}
          onConfirm={() =>
            remove(deleting._id, { onSuccess: () => setDeleting(null) })
          }
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}
